import { colors } from '@/theme/foundations/colors';
import { fontSizes } from '@/theme/foundations/fontSizes';
import { fontWeights } from '@/theme/foundations/fontWeights';

export const customFilterStyles = (newStyles = []) => {
  const defaultStyles = {
    control: (base, state, newStyles) => {
      return {
        ...base,
        minHeight: '41px',
        padding: state.selectProps?.hasSearchIcon ? '0 12px' : '0px',
        fontSize: fontSizes['sm'],
        borderRadius: '6px',
        color: colors['ats-text-light'],
        border: state.selectProps?.border === false ? 0 : '1px solid',
        background: state.isDisabled
          ? colors['secondary-50']
          : colors['base-white'],
        borderColor: `${
          state.isFocused ? colors['secondary-300'] : colors['secondary-100']
        } !important`,
        boxShadow: 'none',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        '&:hover': {
          cursor: state.isDisabled ? 'not-allowed' : 'pointer',
          border: '1px solid',
          borderColor: `${colors['secondary-300']} !important`,
          background: `${colors['secondary-25']}`,
        },
        ...newStyles,
      };
    },

    singleValue: (base, state, newStyles) => ({
      ...base,
      fontSize: fontSizes['sm'],
      fontWeight: fontWeights['regular'],
      paddingLeft: '9px',
      color: state.isDisabled
        ? colors['secondary-500']
        : colors['secondary-700'],
      // Truncate long labels with ellipsis (~28 chars at sm font).
      maxWidth: '200px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      ...newStyles,
    }),

    multiValue: (base, state, newStyles) => ({
      ...base,
      background: 'var(--kula-colors-white, #FFF)',
      borderRadius: '6px',
      // padding: '0 2px',
      paddingLeft: '0px',
      border: '1px solid var(--kula-dark-secondary-100, #EFECF0)',
      // boxShadow: '0 1px 2px 0 #101828',
      color: state.isDisabled
        ? colors['secondary-500']
        : colors['secondary-700'],
      fontSize: fontSizes['sm'],
      fontWeight: fontWeights['medium'],
      // Let the chip shrink so the inner label's max-width + ellipsis can
      // take effect. Without minWidth:0 a flex child reports its natural
      // (un-truncated) content width, which makes long-named chips wrap to
      // their own row instead of flowing alongside neighbours.
      minWidth: 0,
      flexShrink: 0,
      ...newStyles,
    }),

    multiValueLabel: (base, state, newStyles) => ({
      ...base,
      fontSize: fontSizes['sm'],
      padding: '4px',
      fontWeight: fontWeights['medium'],
      color: colors['secondary-700'],
      // Truncate long chip labels with ellipsis (~24 chars at sm font).
      maxWidth: '200px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      ...newStyles,
    }),
    multiValueRemove: (base, state, newStyles) => ({
      ...base,
      '&:hover': {
        background: 'transparent',
        color: colors['secondary-900'],
      },
      ...newStyles,
    }),

    menu: (base, state, newStyles) => ({
      ...base,
      border: '1px solid var(--kula-dark-secondary-100, #EDEDED)',
      boxShadow: '0px 4px 12px 6px rgba(46, 47, 48, 0.06)',
      padding: '4px 8px',
      ...newStyles,
    }),

    menuList: (base, state, newStyles) => ({
      ...base,
      maxHeight: '200px',
      ...newStyles,
    }),

    option: (base, state, newStyles) => ({
      ...base,
      fontSize: '12px',
      fontWeight: 500,
      cursor: 'pointer',
      color: colors.light['text-primary'],
      background:
        state.isSelected || state.isFocused ? colors['hover-light'] : '#FFF',
      '&:hover': {
        background: colors['hover-light'],
      },
      borderRadius: '6px',
      ...newStyles,
    }),

    loadingMessage: (base, state, newStyles) => ({
      ...base,
      padding: 0,
      ...newStyles,
    }),

    dropdownIndicator: (base, state, newStyles) => ({
      ...base,
      color: state?.isDisabled
        ? colors['secondary-500']
        : colors['secondary-700'],

      '& svg': {
        width: '16px',
        height: '16px',
        strokeWidth: 0.05,
      },
      ...newStyles,
    }),

    valueContainer: (base, state, newStyles) => ({
      ...base,
      paddingLeft: '3px',
      // Pin row direction + wrap so chips flow horizontally first and only
      // wrap to a new line when the row genuinely runs out of space.
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      ...newStyles,
    }),

    menuPortal: (base, state, newStyles) => ({
      ...base,
      zIndex: 9999,
      ...newStyles,
    }),

    input: (baseStyles, state, newStyles) => ({
      ...baseStyles,
      color: colors['secondary-700'],
      ...newStyles,
    }),

    placeholder: (baseStyles, state, newStyles) => ({
      ...baseStyles,
      color: colors['secondary-500'],
      paddingLeft: '9px',
      ...newStyles,
    }),
  };

  if (newStyles?.length === 0) return defaultStyles;

  const styledDefaultStyles = {};

  for (const type in defaultStyles) {
    const styledFunction = defaultStyles[type];
    const relevantNewStyles = newStyles?.find(
      (style) => style?.type === type
    )?.newStyles;
    styledDefaultStyles[type] = (base, state) =>
      styledFunction(base, state, relevantNewStyles);
  }

  return styledDefaultStyles;
};
